/**
 * Units registry with dimensional checking.
 *
 * The point is to catch `usd + jobs` at validate time rather than shipping it.
 * Dimensions are tracked as a sparse exponent map, so usd/year × year = usd
 * falls out of the arithmetic instead of needing a lookup table.
 */

import type { UnitId } from './schema.js';

export type Dimension = Record<string, number>;

interface UnitDef {
  dim: Dimension;
  /** Short suffix for analyst mode. */
  abbr: string;
  format: (n: number) => string;
  /**
   * True when `format` already carries the unit in the string — a leading `$`
   * or a trailing `%`. Everything else renders as a bare number and needs its
   * unit shown alongside, or the reader gets "55.5" with no idea of what.
   */
  selfLabelling?: true;
}

const nf = (opts: Intl.NumberFormatOptions) => new Intl.NumberFormat('en-US', opts);

const int = nf({ maximumFractionDigits: 0 });
const one = nf({ maximumFractionDigits: 1 });
const two = nf({ maximumFractionDigits: 2 });
const cents = nf({ minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** $1,234 / $1.2M / $1.27B — chosen by magnitude, never mixed within a chart. */
export function formatUsd(n: number): string {
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1e9) return `${sign}$${one.format(abs / 1e9)}B`;
  if (abs >= 1e6) return `${sign}$${one.format(abs / 1e6)}M`;
  if (abs >= 10_000) return `${sign}$${int.format(abs)}`;
  return `${sign}$${int.format(abs)}`;
}

function formatCount(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1e6) return `${one.format(n / 1e6)}M`;
  if (abs >= 10_000) return int.format(n);
  if (abs < 10) return one.format(n);
  return int.format(n);
}

const UNITS: Record<UnitId, UnitDef> = {
  usd: { dim: { usd: 1 }, abbr: '$', format: formatUsd, selfLabelling: true },
  usd_per_year: { dim: { usd: 1, year: -1 }, abbr: '$/yr', format: formatUsd, selfLabelling: true },
  count: { dim: { count: 1 }, abbr: '', format: formatCount },
  ratio: { dim: {}, abbr: '', format: (n) => two.format(n) },
  percent: {
    dim: {},
    abbr: '%',
    format: (n) => `${one.format(n * 100)}%`,
    selfLabelling: true,
  },
  year: { dim: { year: 1 }, abbr: 'yr', format: (n) => int.format(n) },
  years: { dim: { year: 1 }, abbr: 'yrs', format: (n) => one.format(n) },
  mw: { dim: { mw: 1 }, abbr: 'MW', format: (n) => int.format(n) },
  mwh: { dim: { mwh: 1 }, abbr: 'MWh', format: formatCount },
  mwh_per_year: { dim: { mwh: 1, year: -1 }, abbr: 'MWh/yr', format: formatCount },
  lb_per_mwh: { dim: { lb: 1, mwh: -1 }, abbr: 'lb/MWh', format: (n) => one.format(n) },
  short_tons: { dim: { ton: 1 }, abbr: 'tons', format: formatCount },
  short_tons_per_year: {
    dim: { ton: 1, year: -1 },
    abbr: 'tons/yr',
    format: formatCount,
  },
  gallons_per_day: { dim: { gal: 1, day: -1 }, abbr: 'gal/day', format: int.format },
  million_gallons_per_year: {
    dim: { mgal: 1, year: -1 },
    abbr: 'MG/yr',
    format: (n) => one.format(n),
  },
  acres: { dim: { acre: 1 }, abbr: 'acres', format: formatCount },
  dba: { dim: { dba: 1 }, abbr: 'dBA', format: (n) => one.format(n) },
  hours: { dim: { hour: 1 }, abbr: 'hrs', format: formatCount },
  job_years: { dim: { job_year: 1 }, abbr: 'job-years', format: formatCount },
  teacher_years: { dim: { teacher_year: 1 }, abbr: 'teacher-years', format: (n) => one.format(n) },
  // The divisor units. Making these first-class is what lets the dimensional
  // check prove that "$500,000 ÷ cost-per-teacher" really is teacher-years,
  // rather than a dimensionless ratio we relabelled by hand.
  usd_per_teacher_year: {
    dim: { usd: 1, teacher_year: -1 },
    abbr: '$/teacher-yr',
    format: formatUsd,
    selfLabelling: true,
  },
  apprentice_years: {
    dim: { apprentice_year: 1 },
    abbr: 'apprentice-years',
    format: formatCount,
  },
  usd_per_job_year: { dim: { usd: 1, job_year: -1 }, abbr: '$/job-yr', format: formatUsd, selfLabelling: true },
  hours_per_job_year: {
    dim: { hour: 1, job_year: -1 },
    abbr: 'h/job-yr',
    format: (n) => int.format(n),
  },
  hours_per_apprentice_year: {
    dim: { hour: 1, apprentice_year: -1 },
    abbr: 'h/apprentice-yr',
    format: (n) => int.format(n),
  },
  // MW × (MWh/yr per MW) = MWh/yr, and lb × (tons per lb) = tons.
  hours_per_year: {
    dim: { mwh: 1, year: -1, mw: -1 },
    abbr: 'h/yr',
    format: (n) => int.format(n),
  },
  short_tons_per_lb: { dim: { ton: 1, lb: -1 }, abbr: 'tons/lb', format: (n) => String(n) },
  // Dividing an annual figure by this converts it to a monthly one, and the
  // dimensions carry that: usd·yr⁻¹ ÷ (month·yr⁻¹) = usd·month⁻¹.
  months_per_year: {
    dim: { month: 1, year: -1 },
    abbr: 'mo/yr',
    format: (n) => int.format(n),
  },
  people: { dim: { person: 1 }, abbr: 'people', format: formatCount },
  customers: { dim: { customer: 1 }, abbr: 'customers', format: formatCount },
  // The currency symbol is in the value but the denominator is not, so these
  // two are only half self-labelling: "$2.00" does not say per what. The unit
  // label carries the basis.
  usd_per_mwh: { dim: { usd: 1, mwh: -1 }, abbr: 'per MWh', format: (n) => `$${cents.format(n)}` },
  // A household's monthly bill. Always two decimals, even when significant
  // figures would drop them: this is the one figure a reader checks against the
  // envelope on their kitchen table, and "$2" reads as a different claim from
  // "$2.00". The approximation sign and the range carry the imprecision.
  usd_per_customer_month: {
    dim: { usd: 1, customer: -1, month: -1 },
    abbr: 'a month',
    format: (n) => `$${cents.format(n)}`,
  },
  sq_ft: { dim: { sq_ft: 1 }, abbr: 'sq ft', format: formatCount },
  dimensionless: { dim: {}, abbr: '', format: (n) => two.format(n) },
};

export function unitDef(u: UnitId): UnitDef {
  const d = UNITS[u];
  if (!d) throw new Error(`Unknown unit: ${u}`);
  return d;
}

export function formatValue(n: number, unit: UnitId): string {
  return unitDef(unit).format(n);
}

export function unitAbbr(unit: UnitId): string {
  return unitDef(unit).abbr;
}

// ------------------------------------------------------- dimensional algebra

export function dimOf(unit: UnitId): Dimension {
  return { ...unitDef(unit).dim };
}

export function dimMul(a: Dimension, b: Dimension): Dimension {
  const out: Dimension = { ...a };
  for (const [k, v] of Object.entries(b)) {
    const next = (out[k] ?? 0) + v;
    if (next === 0) delete out[k];
    else out[k] = next;
  }
  return out;
}

export function dimDiv(a: Dimension, b: Dimension): Dimension {
  const inv: Dimension = {};
  for (const [k, v] of Object.entries(b)) inv[k] = -v;
  return dimMul(a, inv);
}

export function dimEqual(a: Dimension, b: Dimension): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of keys) if ((a[k] ?? 0) !== (b[k] ?? 0)) return false;
  return true;
}

export function dimToString(d: Dimension): string {
  const entries = Object.entries(d).sort(([a], [b]) => a.localeCompare(b));
  if (entries.length === 0) return 'dimensionless';
  return entries.map(([k, v]) => (v === 1 ? k : `${k}^${v}`)).join('·');
}

/** Find a UnitId matching a dimension, if one exists. Used for error messages. */
export function unitForDim(d: Dimension): UnitId | undefined {
  for (const [id, def] of Object.entries(UNITS)) {
    if (dimEqual(def.dim, d)) return id as UnitId;
  }
  return undefined;
}

// ------------------------------------------------------------ precision

/**
 * Round to significant figures. Applied once, at the single render call.
 *
 * Without this you publish $47,318,442 off a ±40% range and lose the argument
 * in one screenshot.
 */
export function roundToSigFigs(n: number, sigFigs: number): number {
  if (n === 0 || !Number.isFinite(n)) return n;
  const s = Math.max(1, Math.min(15, Math.floor(sigFigs)));
  // toPrecision, not multiply-round-divide: the latter reintroduces binary
  // float error at exactly the magnitudes this tool publishes at
  // (47_318_442 → 47299999.99999999 rather than 47_300_000).
  return Number(n.toPrecision(s));
}

/** True when `formatValue` already puts the unit in the string. */
export function isSelfLabelling(unit: UnitId): boolean {
  return unitDef(unit).selfLabelling === true;
}
